import React, { useState, useEffect } from "react";
import { FaEye, FaDownload, FaHeart, FaUsers } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { collection, query, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../backend/firebase";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import "./DatasetAnalytics.css";

const DatasetAnalytics = ({ datasetid }) => {
  const [analyticsData, setAnalyticsData] = useState({
    views: [],
    downloads: [],
    likes: [],
    users: [],
  });
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState("line");

  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Analytics data processing
  const processAnalyticsData = (views, downloads, likes) => {
    const now = new Date();
    let daysToShow = 7;

    switch (analyticsTimeRange) {
      case "7d":
        daysToShow = 7;
        break;
      case "30d":
        daysToShow = 30;
        break;
      case "90d":
        daysToShow = 90;
        break;
      default:
        daysToShow = 7;
    }

    const dates = [];
    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split("T")[0]);
    }

    const viewsByDate = {};
    const downloadsByDate = {};
    const likesByDate = {};

    dates.forEach((date) => {
      viewsByDate[date] = 0;
      downloadsByDate[date] = 0;
      likesByDate[date] = 0;
    });

    // Process views
    views.forEach((view) => {
      const viewDate = new Date(view.viewedOn).toISOString().split("T")[0];
      if (viewsByDate[viewDate] !== undefined) {
        viewsByDate[viewDate]++;
      }
    });

    // Process downloads
    downloads.forEach((download) => {
      const downloadDate = new Date(download.downloadedAt)
        .toISOString()
        .split("T")[0];
      if (downloadsByDate[downloadDate] !== undefined) {
        downloadsByDate[downloadDate]++;
      }
    });

    // Process likes
    likes.forEach((like) => {
      const likeDate = new Date(like.likedAt).toISOString().split("T")[0];
      if (likesByDate[likeDate] !== undefined) {
        likesByDate[likeDate]++;
      }
    });

    const processedData = dates.map((date) => ({
      date,
      views: viewsByDate[date],
      downloads: downloadsByDate[date],
      likes: likesByDate[date],
      shortDate: formatShortDate(date),
      fullDate: new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: daysToShow > 30 ? "numeric" : undefined,
      }),
    }));

    return processedData;
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="analytics-tooltip">
          <p className="tooltip-date">{label}</p>
          {payload.map((entry, index) => (
            <p
              key={index}
              className="tooltip-item"
              style={{ color: entry.color }}
            >
              {entry.name}: <strong>{entry.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Single metric tooltip
  const SingleMetricTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="analytics-tooltip">
          <p className="tooltip-date">{label}</p>
          <p className="tooltip-item" style={{ color: "#667eea" }}>
            {payload[0].name}: <strong>{payload[0].value}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);

        // Fetch views
        const viewsQuery = query(
          collection(db, "datasets", datasetid, "Views")
        );
        const viewsSnapshot = await getDocs(viewsQuery);
        const viewsData = viewsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch downloads
        const downloadsQuery = query(
          collection(db, "datasets", datasetid, "downloads")
        );
        const downloadsSnapshot = await getDocs(downloadsQuery);
        const downloadsData = downloadsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch likes
        const likesQuery = query(
          collection(db, "datasets", datasetid, "likes")
        );
        const likesSnapshot = await getDocs(likesQuery);
        const likesData = likesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch unique users
        const uniqueUsers = new Set([
          ...viewsData.map((v) => v.id),
          ...downloadsData.map((d) => d.id),
          ...likesData.map((l) => l.id),
        ]);

        setAnalyticsData({
          views: viewsData,
          downloads: downloadsData,
          likes: likesData,
          users: Array.from(uniqueUsers),
        });
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [datasetid]);

  // Real-time updates for analytics
  useEffect(() => {
    const viewsQuery = query(collection(db, "datasets", datasetid, "Views"));
    const downloadsQuery = query(
      collection(db, "datasets", datasetid, "downloads")
    );
    const likesQuery = query(collection(db, "datasets", datasetid, "likes"));

    const unsubscribeViews = onSnapshot(viewsQuery, (snapshot) => {
      const viewsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAnalyticsData((prev) => ({ ...prev, views: viewsData }));
    });

    const unsubscribeDownloads = onSnapshot(downloadsQuery, (snapshot) => {
      const downloadsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAnalyticsData((prev) => ({ ...prev, downloads: downloadsData }));
    });

    const unsubscribeLikes = onSnapshot(likesQuery, (snapshot) => {
      const likesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAnalyticsData((prev) => ({ ...prev, likes: likesData }));
    });

    return () => {
      unsubscribeViews();
      unsubscribeDownloads();
      unsubscribeLikes();
    };
  }, [datasetid]);

  if (loading) {
    return (
      <div className="dataset-analytics-loading">
        <ClipLoader color="#ffffff" size={30} />
        <span className="loading-text">Loading analytics...</span>
      </div>
    );
  }

  const totalViews = analyticsData.views.length;
  const totalDownloads = analyticsData.downloads.length;
  const totalLikes = analyticsData.likes.length;
  const totalUsers = analyticsData.users.length;

  // Process analytics data for the graph
  const processedAnalytics = processAnalyticsData(
    analyticsData.views,
    analyticsData.downloads,
    analyticsData.likes
  );

  const calculateTrend = (data, field) => {
    if (data.length < 2) return 0;
    const recent = data.slice(-7).reduce((sum, day) => sum + day[field], 0);
    const previous = data
      .slice(-14, -7)
      .reduce((sum, day) => sum + day[field], 0);
    if (previous === 0) return recent > 0 ? 100 : 0;
    return ((recent - previous) / previous) * 100;
  };

  const viewsTrend = calculateTrend(processedAnalytics, "views");
  const downloadsTrend = calculateTrend(processedAnalytics, "downloads");
  const likesTrend = calculateTrend(processedAnalytics, "likes");

  // Render chart based on type for single metric
  const renderChart = (data, dataKey, name) => {
    const chartProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const commonProps = {
      stroke: "#667eea",
      fill: "#667eea",
      strokeWidth: 2,
      name,
    };

    switch (chartType) {
      case "line":
        return (
          <LineChart {...chartProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis
              dataKey="fullDate"
              stroke="#ccc"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis stroke="#ccc" fontSize={12} />
            <Tooltip content={<SingleMetricTooltip />} />
            <Line
              type="monotone"
              dataKey={dataKey}
              {...commonProps}
              dot={{ fill: "#667eea", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: "#667eea" }}
            />
          </LineChart>
        );
      case "area":
        return (
          <AreaChart {...chartProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis
              dataKey="fullDate"
              stroke="#ccc"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis stroke="#ccc" fontSize={12} />
            <Tooltip content={<SingleMetricTooltip />} />
            <Area
              type="monotone"
              dataKey={dataKey}
              {...commonProps}
              fillOpacity={0.6}
            />
          </AreaChart>
        );
      case "bar":
        return (
          <BarChart {...chartProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis
              dataKey="fullDate"
              stroke="#ccc"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis stroke="#ccc" fontSize={12} />
            <Tooltip content={<SingleMetricTooltip />} />
            <Bar dataKey={dataKey} {...commonProps} fillOpacity={0.8} />
          </BarChart>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dataset-analytics-container">
      <div className="dataset-analytics-header">
        <div className="header-left">
        </div>
        <div className="header-controls">
          <div className="chart-type-selector">
            <button
              onClick={() => setChartType("line")}
              className={`chart-type-btn ${
                chartType === "line" ? "active" : ""
              }`}
            >
              Line
            </button>
            <button
              onClick={() => setChartType("area")}
              className={`chart-type-btn ${
                chartType === "area" ? "active" : ""
              }`}
            >
              Area
            </button>
            <button
              onClick={() => setChartType("bar")}
              className={`chart-type-btn ${
                chartType === "bar" ? "active" : ""
              }`}
            >
              Bar
            </button>
          </div>
          <div className="analytics-time-range-selector">
            {["7d", "30d", "90d"].map((range) => (
              <button
                key={range}
                onClick={() => setAnalyticsTimeRange(range)}
                className={`time-range-button ${
                  analyticsTimeRange === range ? "active" : ""
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary-cards">
        <div className="analytics-card">
          <div className="analytics-card-content">
            <div className="analytics-card-value">{totalViews}</div>
            <div className="analytics-card-label">Total Views</div>
          </div>
        </div>
        <div className="analytics-card">
          <div className="analytics-card-content">
            <div className="analytics-card-value">{totalDownloads}</div>
            <div className="analytics-card-label">Total Downloads</div>
          </div>
        </div>
        <div className="analytics-card">
          <div className="analytics-card-content">
            <div className="analytics-card-value">{totalLikes}</div>
            <div className="analytics-card-label">Total Likes</div>
          </div>
        </div>

      </div>
      <div className="individual-charts-grid">
        <div className="analytics-chart-container individual-chart">
          <div className="chart-header">
            <span className="chart-header-heading">
              Views 
            </span>
            <span className="chart-total">Total: <b>{totalViews}</b></span>
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={250}>
              {renderChart(processedAnalytics, "views", "Views")}
            </ResponsiveContainer>
          </div>
        </div>
        <div className="analytics-chart-container individual-chart">
          <div className="chart-header">
            <span className="chart-header-heading">
              
              Downloads 
            </span>
            <span className="chart-total">Total: <b>{totalDownloads}</b></span>
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={250}>
              {renderChart(processedAnalytics, "downloads", "Downloads")}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Likes Chart */}
        <div className="analytics-chart-container individual-chart">
          <div className="chart-header">
            <span className="chart-header-heading">
              Likes 
            </span>
            <span className="chart-total">Total: <b>{totalLikes}</b></span>
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={250}>
              {renderChart(processedAnalytics, "likes", "Likes")}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatasetAnalytics;
